import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    // No token → user is definitely not logged in. Skip the network call entirely.
    if (!localStorage.getItem("hx_token")) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      localStorage.removeItem("hx_token");
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("hx_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const startGhostSession = async (advertiserId) => {
    try {
      const activeToken = localStorage.getItem("hx_token");
      const { data } = await api.post(`/admin/advertisers/${advertiserId}/ghost-session`);
      if (activeToken && !sessionStorage.getItem("hx_admin_token") && !user?.ghost_session) {
        sessionStorage.setItem("hx_admin_token", activeToken);
      }
      if (data.access_token) localStorage.setItem("hx_token", data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const exitGhostSession = async () => {
    const adminToken = sessionStorage.getItem("hx_admin_token");
    if (!adminToken) {
      await logout();
      return { ok: false };
    }
    localStorage.setItem("hx_token", adminToken);
    sessionStorage.removeItem("hx_admin_token");
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return { ok: true, user: data };
    } catch {
      localStorage.removeItem("hx_token");
      setUser(null);
      return { ok: false };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("hx_token");
    sessionStorage.removeItem("hx_admin_token");
    setUser(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (data.user) setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, refresh, changePassword, startGhostSession, exitGhostSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
